import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import NavigationParticuliers from '@/components/NavigationParticuliers';
import Footer from '@/components/Footer';
import { ScrollReveal } from '@/components/ScrollReveal';
import UniformCard from '@/components/UniformCard';
import BackToTop from '@/components/BackToTop';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import { Tent, ArrowRight, ChevronDown, ChevronUp, Euro, Download } from 'lucide-react';

const Reserver = () => {
  const navigate = useNavigate();
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);

  const toggleQuestion = (questionId: string) => {
    setOpenQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId) 
        : [...prev, questionId]
    );
  };

  const parcours = [
    {
      id: 'camping',
      title: 'Je réserve dans mon camping',
      description: 'Accédez à la réservation vélo de votre établissement.',
      image: './images/camping_reservation_illustration_20260121_094944.png',
      tags: ['Camping', 'Réservation en ligne', 'Sur place'],
      cta: 'Choisir mon camping',
      action: 'redirect'
    },
    {
      id: 'saisonnier',
      title: 'Je suis étudiant / saisonnier',
      description: 'Demande de vélo mensuel + entretien inclus.',
      image: './images/student_seasonal_worker_illustration_20260121_094943.png',
      tags: ['Abonnement mensuel', 'À partir de 30€', 'Sur zones couvertes'],
      cta: 'Faire une demande',
      action: 'form'
    },
    {
      id: 'livraison',
      title: 'Je souhaite une livraison',
      description: 'Livraison vélo sur certains secteurs (Île de Ré, Montalivet).',
      image: './images/bike_delivery_illustration_20260121_094944.png',
      tags: ['Livraison', 'Sur RDV', 'Zones limitées'],
      cta: 'Faire une demande',
      action: 'form'
    },
    {
      id: 'groupe',
      title: 'Groupe & séminaire',
      description: 'Une sortie, un séminaire, un événement d’entreprise ? Nous organisons votre parc vélo sur-mesure avec livraison et maintenance incluses.',
      image: './images/group_seminar_illustration_20260121_094943.png',
      tags: ['Devis', 'Quantités', 'Organisation'],
      cta: 'Demander un devis',
      action: 'form'
    }
  ];

  const faqQuestions = [
    {
      id: 'camping-introuvable',
      q: 'Mon camping n\'est pas dans la liste ?',
      a: 'Contactez-nous ! Nous développons régulièrement de nouveaux partenariats. Votre camping peut nous rejoindre facilement.'
    },
    {
      id: 'tarifs-variables',
      q: 'Pourquoi les tarifs varient-ils selon le camping ?',
      a: 'Chaque établissement adapte ses tarifs selon ses services (maintenance, assurance, proximité). Nous garantissons la transparence.'
    },
    {
      id: 'reservation-avance',
      q: 'Puis-je réserver à l\'avance ?',
      a: 'Oui ! La réservation en ligne est disponible jusqu\'à 3 mois à l\'avance selon les campings partenaires.'
    },
    {
      id: 'annulation',
      q: 'Puis-je annuler ma réservation ?',
      a: 'Les conditions d\'annulation dépendent de chaque camping. Consultez les conditions lors de votre réservation.'
    }
  ];

  const handleParcoursClick = (parcours: any) => {
    if (parcours.action === 'redirect') {
      navigate('/choisir-camping');
    } else if (parcours.action === 'form') {
      if (parcours.id === 'saisonnier') {
        navigate('/demande-saisonnier');
      } else if (parcours.id === 'livraison') {
        navigate('/demande-livraison');
      } else if (parcours.id === 'groupe') {
        navigate('/demande-groupe');
      }
    }
  };

  const QuestionAccordion = ({ question, isOpen, onToggle }: any) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-slate-900">{question.q}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-slate-700 leading-relaxed">{question.a}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <NavigationParticuliers />
      
      {/* 1) HEADER (HERO) */}
      <section className="bg-gradient-to-b from-primary to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Tent className="h-8 w-8" />
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              Réserver un vélo
            </h1>
            <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto mb-8">
              Choisissez votre situation. On vous guide en 10 secondes.
            </p>
            
            {/* Bouton principal téléchargement tarifs */}
            <Button asChild size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold px-8 py-4 rounded-xl shadow-lg">
              <a href="/documents/guide-tarifaire-les-hirondelles.pdf" target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 h-5 w-5" />
                Télécharger les tarifs complets (PDF)
              </a>
            </Button>
            <p className="text-sm text-primary-foreground/70 mt-3">
              Tarifs officiels – mis à jour par établissement
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* 2) CHOISISSEZ VOTRE SITUATION */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Choisissez votre situation
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Sélectionnez l'option qui correspond le mieux à vos besoins
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {parcours.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 100}>
                <div 
                  onClick={() => handleParcoursClick(item)}
                  className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                >
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Button className="btn-primary w-full">
                      {item.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3) MICRO FAQ */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-lg text-slate-600">
              Les réponses aux questions les plus courantes
            </p>
          </ScrollReveal>

          <div className="space-y-4">
            {faqQuestions.map((question, index) => (
              <ScrollReveal key={question.id} delay={index * 100}>
                <QuestionAccordion 
                  question={question} 
                  isOpen={openQuestions.includes(question.id)} 
                  onToggle={() => toggleQuestion(question.id)} 
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <StickyReservationCTA />
      <BackToTop />
      <Footer />
    </div>
  );
};

export default Reserver;